import React from 'react';

class NotificationManager {
    constructor() {
        this.notifications = [];
        this.listeners = [];
    }

    // Add a notification
    addNotification(type, title, message, duration = 5000) {
        const notification = {
            id: Date.now() + Math.random(),
            type, // 'success', 'error', 'warning', 'info'
            title,
            message,
            duration,
            timestamp: new Date()
        };

        this.notifications.push(notification);
        this.notifyListeners();

        // Auto-remove after duration
        if (duration > 0) {
            setTimeout(() => {
                this.removeNotification(notification.id);
            }, duration);
        }

        return notification.id;
    }

    // Remove a notification
    removeNotification(id) {
        this.notifications = this.notifications.filter(n => n.id !== id);
        this.notifyListeners();
    }

    // Get all notifications
    getNotifications() {
        return [...this.notifications];
    }

    // Clear all notifications
    clearAll() {
        this.notifications = [];
        this.notifyListeners();
    }

    // Subscribe to notification changes
    subscribe(listener) {
        this.listeners.push(listener);
        return () => {
            this.listeners = this.listeners.filter(l => l !== listener);
        };
    }

    // Notify all listeners
    notifyListeners() {
        this.listeners.forEach(listener => listener(this.notifications));
    }

    // Convenience methods
    success(title, message, duration) {
        return this.addNotification('success', title, message, duration);
    }

    error(title, message, duration) {
        return this.addNotification('error', title, message, duration);
    }

    warning(title, message, duration) {
        return this.addNotification('warning', title, message, duration);
    }

    info(title, message, duration) {
        return this.addNotification('info', title, message, duration);
    }

    // Session-specific notifications
    sessionExpired() {
        return this.warning(
            'Session Expired',
            'Your login session has expired. Please log in again to continue.',
            0 // Don't auto-remove
        );
    }

    networkError() {
        return this.error(
            'Network Error',
            'Unable to connect to the server. Please check your internet connection and try again.',
            8000
        );
    }

    apiError(message) {
        return this.error(
            'Request Failed',
            message || 'An error occurred while processing your request. Please try again.',
            6000
        );
    }
}

// Create a singleton instance
const notificationManager = new NotificationManager();

// React component to display notifications (converted to class component for React 15 compatibility)
export class NotificationContainer extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            notifications: []
        };
        this.unsubscribe = null;
    }

    componentDidMount() {
        this.unsubscribe = notificationManager.subscribe((notifications) => {
            this.setState({ notifications });
        });
    }

    componentWillUnmount() {
        if (this.unsubscribe) {
            this.unsubscribe();
        }
    }

    getPositionStyles() {
        const { position = 'top-right' } = this.props;
        const base = {
            position: 'fixed',
            zIndex: 9999,
            padding: '20px',
            pointerEvents: 'none'
        };

        switch (position) {
            case 'top-right':
                return { ...base, top: 0, right: 0 };
            case 'top-left':
                return { ...base, top: 0, left: 0 };
            case 'bottom-right':
                return { ...base, bottom: 0, right: 0 };
            case 'bottom-left':
                return { ...base, bottom: 0, left: 0 };
            case 'top-center':
                return { ...base, top: 0, left: '50%', transform: 'translateX(-50%)' };
            case 'bottom-center':
                return { ...base, bottom: 0, left: '50%', transform: 'translateX(-50%)' };
            default:
                return { ...base, top: 0, right: 0 };
        }
    }

    getNotificationStyles(type) {
        const base = {
            marginBottom: '10px',
            padding: '15px',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            maxWidth: '400px',
            pointerEvents: 'auto',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            border: '1px solid'
        };

        switch (type) {
            case 'success':
                return {
                    ...base,
                    backgroundColor: '#d4edda',
                    borderColor: '#c3e6cb',
                    color: '#155724'
                };
            case 'error':
                return {
                    ...base,
                    backgroundColor: '#f8d7da',
                    borderColor: '#f5c6cb',
                    color: '#721c24'
                };
            case 'warning':
                return {
                    ...base,
                    backgroundColor: '#fff3cd',
                    borderColor: '#ffeaa7',
                    color: '#856404'
                };
            case 'info':
                return {
                    ...base,
                    backgroundColor: '#d1ecf1',
                    borderColor: '#bee5eb',
                    color: '#0c5460'
                };
            default:
                return base;
        }
    }

    render() {
        const { notifications } = this.state;

        if (notifications.length === 0) {
            return null;
        }

        return (
            <div style={this.getPositionStyles()}>
                {notifications.map(notification => (
                    <div
                        key={notification.id}
                        style={this.getNotificationStyles(notification.type)}
                        onClick={() => notificationManager.removeNotification(notification.id)}
                    >
                        <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                            {notification.title}
                        </div>
                        <div style={{ fontSize: '14px' }}>
                            {notification.message}
                        </div>
                        <div style={{
                            fontSize: '12px',
                            marginTop: '8px',
                            opacity: 0.7
                        }}>
                            Click to dismiss
                        </div>
                    </div>
                ))}
            </div>
        );
    }
}

export default notificationManager; 